import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LookupListDto, LookupValueDto } from '@tww/shared';
import { api } from '../../api/client';
import { useLookups } from '../../api/hooks';
import { Modal } from '../../ui/Modal';
import { ErrorState, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { Checkbox, Select, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';
import { moveItem, slugKey, uniqueKey } from './adminUtil';
import { SortCell } from './SortCell';

/** Beschreibungen der Systemlisten für die Oberfläche */
const LIST_DESC: Record<string, string | null> = {
  states: 'Auswahl für Kontakte, Flächen und Aktionen',
  regions: 'Einsatzregionen; neue Regionen aus Kontakten kommen automatisch hinzu',
  action_types: 'Das Kürzel steht vorn in der Aktionskennung. Typen mit Monitoring erscheinen später im Monitoring.',
  action_status: 'Status und Farben in Listen und Kalender',
  tree_species: null,
};

type Col = { label: string; render: (v: LookupValueDto) => ReactNode };
const STATUS_LISTS = ['action_status', 'rating_scale'];

/** Stammlisten: Liste links, Werte rechts. Werte werden inaktiv gesetzt statt gelöscht, Reihenfolge per Ziehen oder Pfeilen. */
export function LookupsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useLookups();
  const [active, setActive] = useState<string>('');
  const [editing, setEditing] = useState<Partial<LookupValueDto> | null>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const list = q.data?.find((l) => l.key === (active || q.data?.[0]?.key));
  const inv = () => qc.invalidateQueries({ queryKey: ['lookups'] });
  const save = useMutation({
    mutationFn: (v: Partial<LookupValueDto>) => {
      const body = { label: v.label, color: v.color || null, meta: v.meta && Object.keys(v.meta).length ? v.meta : null };
      return v.id ? api.patch(`/lookups/${list!.key}/values/${v.id}`, body) : api.post(`/lookups/${list!.key}/values`, { ...body, code: v.code });
    },
    onSuccess: () => {
      inv();
      setEditing(null);
      toast('Gespeichert');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const toggle = useMutation({
    mutationFn: (v: LookupValueDto) => api.patch(`/lookups/${list!.key}/values/${v.id}`, { is_active: !v.is_active }),
    onSuccess: (_r, v) => {
      inv();
      toast(v.is_active ? `„${v.label}“ deaktiviert` : `„${v.label}“ aktiviert`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const order = useMutation({
    mutationFn: (ids: number[]) => api.put(`/lookups/${list!.key}/order`, { ids }),
    onSuccess: inv,
    onError: (e: Error) => toast(`Reihenfolge nicht gespeichert: ${e.message}`, 'error'),
  });
  if (q.isLoading) return <Skeleton />;
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  const values = list?.values ?? [];
  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= values.length) return;
    order.mutate(moveItem(values.map((v) => v.id), from, to));
  };
  const states = q.data!.find((l) => l.key === 'states')?.values ?? [];
  const stateName = (code: unknown) => states.find((s) => s.code === code)?.label ?? (code ? String(code) : '');
  const isStatus = STATUS_LISTS.includes(list?.key ?? '');
  const cols = list ? metaColumns(list, stateName) : [];
  const desc = list ? (list.key in LIST_DESC ? LIST_DESC[list.key] : list.description) : null;
  return (
    <div className="grid g-side">
      <div className="panel">
        <div className="pb side-list side-list-pad">
          {q.data!.map((l) => (
            <button key={l.key} type="button" className={l.key === list?.key ? 'on' : ''} onClick={() => setActive(l.key)}>
              <span>{l.name}</span>
              <span className="n">{l.values?.length}</span>
            </button>
          ))}
        </div>
      </div>
      {list && (
        <div className="panel">
          <div className="ph">
            <div>
              <h2>{list.name}</h2>
              {desc && <div className="mut small">{desc}</div>}
            </div>
            <button className="btn sm primary" type="button" onClick={() => setEditing({ code: '', label: '', meta: null })}>
              <Icon name="plus" /> Wert
            </button>
          </div>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th className="col-sort">
                    <span className="sr-only">Reihenfolge</span>
                  </th>
                  <th>Anzeige</th>
                  {isStatus && <th>Farbe</th>}
                  {cols.map((c) => (
                    <th key={c.label}>{c.label}</th>
                  ))}
                  <th>Aktiv</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {values.map((v, i) => (
                  <tr key={v.id} className={v.is_active ? '' : 'row-off'} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag != null) move(drag, i); setDrag(null); }}>
                    <SortCell index={i} count={values.length} label={v.label} onMove={move} busy={order.isPending} />
                    <td>{v.label}</td>
                    {isStatus && <td>{v.color && <span className="swatch" style={{ background: v.color }} title={v.color} />}</td>}
                    {cols.map((c) => (
                      <td key={c.label} className="small">
                        {c.render(v)}
                      </td>
                    ))}
                    <td>
                      <input type="checkbox" checked={v.is_active} onChange={() => toggle.mutate(v)} aria-label={`${v.label} aktiv`} disabled={toggle.isPending} />
                    </td>
                    <td className="num">
                      <button className="btn sm" type="button" onClick={() => setEditing(v)}>
                        Bearbeiten
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {editing && list && <ValueModal list={list} value={editing} states={states} busy={save.isPending} onChange={setEditing} onSave={() => save.mutate(editing)} onClose={() => setEditing(null)} />}
    </div>
  );
}

/** Lesbare Spalten für die Zusatzangaben einer Liste; unbekannte Angaben als „Zusatz“ */
function metaColumns(list: LookupListDto, stateName: (c: unknown) => string): Col[] {
  const values = list.values ?? [];
  switch (list.key) {
    case 'states':
      return [{ label: 'Kurzform', render: (v) => String(v.meta?.short ?? '') }];
    case 'regions':
      return [{ label: 'Bundesland', render: (v) => stateName(v.meta?.state) }];
    case 'action_types':
      return [
        { label: 'Kürzel', render: (v) => String(v.meta?.prefix ?? '') },
        { label: 'Monitoring', render: (v) => (v.meta?.monitoring ? 'ja' : 'nein') },
      ];
    case 'cost_categories':
      return [{ label: 'Kostenblock', render: (v) => (v.meta?.per_tree ? 'Forst (je Baum)' : 'Aktion (Logistik, Event)') }];
    case 'tree_species':
    case 'action_status':
      return [];
    default:
      if (!values.some((v) => v.meta && Object.keys(v.meta).length)) return [];
      return [{ label: 'Zusatz', render: (v) => (v.meta ? Object.entries(v.meta).map(([k, x]) => `${k.replace(/_/g, ' ')}: ${x === true ? 'ja' : x === false ? 'nein' : String(x)}`).join(', ') : '') }];
  }
}

/** Kurzer Eintrag (Anzeige plus höchstens zwei Angaben): schmales Modal */
function ValueModal({ list, value: v, states, busy, onChange, onSave, onClose }: { list: LookupListDto; value: Partial<LookupValueDto>; states: LookupValueDto[]; busy: boolean; onChange: (v: Partial<LookupValueDto>) => void; onSave: () => void; onClose: () => void }) {
  const taken = (list.values ?? []).map((x) => x.code);
  const [codeTouched, setCodeTouched] = useState(false);
  const setMeta = (k: string, x: unknown) => onChange({ ...v, meta: { ...(v.meta ?? {}), [k]: x } });
  const isNew = !v.id;
  return (
    <Modal
      title={isNew ? 'Neuer Wert' : 'Wert bearbeiten'}
      subtitle={list.name}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" type="button" disabled={!v.label?.trim() || !v.code || busy} onClick={onSave}>
            Speichern
          </button>
        </>
      }
    >
      <div className="form one">
        <TextInput label="Anzeige" value={v.label ?? ''} onChange={(x) => onChange({ ...v, label: x, code: isNew && !codeTouched ? uniqueKey(slugKey(x, 50, 'wert'), taken) : v.code })} required autoFocus />
        {STATUS_LISTS.includes(list.key) && <TextInput label="Farbe" type="color" value={v.color || '#7C8A81'} onChange={(x) => onChange({ ...v, color: x.toUpperCase() })} />}
        {list.key === 'regions' && <Select label="Bundesland" value={String(v.meta?.state ?? '')} onChange={(x) => setMeta('state', x || null)} options={states.map((s) => ({ value: s.code, label: s.label }))} placeholder="–" />}
        {list.key === 'states' && <TextInput label="Kurzform" value={String(v.meta?.short ?? '')} onChange={(x) => setMeta('short', x)} />}
        {list.key === 'cost_categories' && <Checkbox label="Forstkosten: zählt in die Kosten je Baum" value={Boolean(v.meta?.per_tree)} onChange={(x) => setMeta('per_tree', x)} />}
        {list.key === 'action_types' && (
          <>
            <TextInput label="Kürzel in der Kennung" value={String(v.meta?.prefix ?? '')} onChange={(x) => setMeta('prefix', x.toUpperCase())} placeholder="PFM" />
            <Checkbox label="Geht ins Monitoring" value={Boolean(v.meta?.monitoring)} onChange={(x) => setMeta('monitoring', x)} />
          </>
        )}
        <details className="fs">
          <summary>Erweitert</summary>
          <div className="body">
            <TextInput label="Code" value={v.code ?? ''} onChange={(x) => (setCodeTouched(true), onChange({ ...v, code: x }))} disabled={!isNew} hint={isNew ? 'Wird aus der Anzeige erzeugt und bleibt danach fest' : 'Fest, wird bei Import und Auswertung verwendet'} />
          </div>
        </details>
      </div>
    </Modal>
  );
}
